/**
 * Wave F (decision 0062): the dated price table and the pricing of usage rows. Pure.
 *
 * Prices are USD per million tokens, which is micro-USD per token, so `tokens × price` is micro-USD. Every figure is
 * an estimate at API list price, never an invoice. The table is versioned: every priced row keeps the
 * `priceTableVersion` it was priced with, and a price change ships as a new version with a cloud deploy.
 *
 * Sources, fetched 2026-10-11:
 * - Claude API pricing  https://platform.claude.com/docs/en/about-claude/pricing (model table, fast mode, web search)
 * - Claude models       https://platform.claude.com/docs/en/about-claude/models/overview (ids)
 * - OpenAI API pricing  https://developers.openai.com/api/docs/pricing (standard tier, ≤ 272k input; Fast multipliers)
 * - Codex pricing       https://learn.chatgpt.com/docs/pricing (credits, Fast, model availability)
 * Sources, fetched 2026-10-15 (table version "2026-10-15", decision 0111):
 * - Gemini API pricing  https://ai.google.dev/gemini-api/docs/pricing (Paid Tier, Standard; output includes thinking;
 *                       page last updated 2026-10-15)
 * - Meta Model API      https://dev.meta.ai/docs/pricing-rate-limits (Standard mode; "No long-context premium")
 * - Cloudflare Containers: see `CONTAINER_PRICES` (its own version, decision 0107).
 */
import type { BillingClass, ComputeUsage, FundedBy, Usage } from "@minga/protocol"
import type { UsageV2 } from "./usage"

type ModelProvider = Usage["provider"]

export const PRICE_TABLE_VERSION = "2026-10-15"

export interface ModelPrice {
  /** Canonical id: "claude-opus-5-5", "gpt-6.1-sol", "gemini-3.8-flash", "muse-spark-1.3". */
  readonly id: string
  readonly provider: ModelProvider
  readonly aliases: ReadonlyArray<string>
  /** USD per million tokens = micro-USD per token. */
  readonly input: number
  readonly cacheRead: number
  /** Anthropic 5-minute writes; OpenAI's single cache-write price goes here (input price when the page lists none). */
  readonly cacheWrite5m: number
  /** Anthropic 1-hour writes; = cacheWrite5m for OpenAI. */
  readonly cacheWrite1h: number
  readonly output: number
  /**
   * Fast mode: explicit input/output prices (Anthropic Opus; cache prices scale with input, as the page says the
   * caching multipliers stack) or one multiplier on every price (OpenAI).
   */
  readonly fast?: { readonly input: number; readonly output: number } | { readonly multiplier: number }
  readonly source: string
}

const ANTHROPIC_SRC = "https://platform.claude.com/docs/en/about-claude/pricing (2026-10-11)"
const OPENAI_SRC = "https://developers.openai.com/api/docs/pricing (2026-10-11)"
const GOOGLE_SRC = "https://ai.google.dev/gemini-api/docs/pricing (2026-10-15)"
const META_SRC = "https://dev.meta.ai/docs/pricing-rate-limits (2026-10-15)"

/** Anthropic row: 5 m writes 1.25×, 1 h writes 2× input (true for every row below; cache reads vary per model). */
const claude = (
  id: string,
  input: number,
  cacheRead: number,
  output: number,
  extra: { aliases?: ReadonlyArray<string>; fast?: { input: number; output: number } } = {},
): ModelPrice => ({
  id,
  provider: "anthropic",
  aliases: extra.aliases ?? [],
  input,
  cacheRead,
  cacheWrite5m: input * 1.25,
  cacheWrite1h: input * 2,
  output,
  ...(extra.fast ? { fast: extra.fast } : {}),
  source: ANTHROPIC_SRC,
})

/** OpenAI row: one cache-write price (absent on the page → input price), Fast 2× unless stated. */
const openai = (
  id: string,
  input: number,
  cacheRead: number,
  cacheWrite: number | null,
  output: number,
  fastMultiplier = 2,
  aliases: ReadonlyArray<string> = [],
): ModelPrice => ({
  id,
  provider: "openai",
  aliases,
  input,
  cacheRead,
  cacheWrite5m: cacheWrite ?? input,
  cacheWrite1h: cacheWrite ?? input,
  output,
  fast: { multiplier: fastMultiplier },
  source: OPENAI_SRC,
})

/**
 * A row of a vendor without cache-write or fast prices (Google, Meta). A cache write costs the input price: neither
 * page lists a write premium (Gemini's explicit-cache storage per hour is not a token price and is not applied).
 * Output includes thinking/reasoning tokens, as both pages bill it and as agy and Muse report it.
 */
const listed = (
  provider: "google" | "meta",
  id: string,
  input: number,
  cacheRead: number,
  output: number,
  aliases: ReadonlyArray<string> = [],
): ModelPrice => ({
  id,
  provider,
  aliases,
  input,
  cacheRead,
  cacheWrite5m: input,
  cacheWrite1h: input,
  output,
  source: provider === "google" ? GOOGLE_SRC : META_SRC,
})

/** Antigravity (`agy models`) names a Gemini model with its thinking level: "gemini-3.8-flash-low". */
const agyLevels = (id: string): ReadonlyArray<string> => ["low", "medium", "high"].map((l) => `${id}-${l}`)

const ANTHROPIC_PRICES: ReadonlyArray<ModelPrice> = [
  // ---- Anthropic (cache reads 0.1× input, except 0.025× on Fable 5.1 and 0.05× on Opus 5.5 / Sonnet 5.5) ----
  claude("claude-fable-5-1", 10, 0.25, 50),
  claude("claude-fable-5", 10, 1, 50),
  claude("claude-opus-5-5", 4, 0.2, 20, { fast: { input: 8, output: 40 } }),
  claude("claude-opus-5", 5, 0.5, 25, { fast: { input: 10, output: 50 } }),
  claude("claude-opus-4-8", 5, 0.5, 25, { fast: { input: 10, output: 50 } }),
  claude("claude-opus-4-7", 5, 0.5, 25),
  claude("claude-opus-4-6", 5, 0.5, 25),
  claude("claude-opus-4-5", 5, 0.5, 25),
  claude("claude-opus-4-1", 15, 1.5, 75), // retired; kept for old rows
  claude("claude-sonnet-5-5", 2, 0.1, 10),
  claude("claude-sonnet-5", 2, 0.2, 10),
  claude("claude-sonnet-4-6", 3, 0.3, 15),
  claude("claude-sonnet-4-5", 3, 0.3, 15),
  // Haiku 5.5: the ≤ 100k-prompt tier. Over 100k (cache reads count) is 5×; totals cannot apply a per-request tier, so
  // a large-context Haiku row priced here can be up to 5× low. Claude runner rows use the CLI's own figure instead.
  claude("claude-haiku-5-5", 0.1, 0.01, 0.5),
  claude("claude-haiku-4-5", 1, 0.1, 5),
]

/**
 * Claude served by Antigravity (rows have provider "google"): Anthropic's own list price for the model (Vertex AI's
 * partner price page could not be read). agy's "-thinking" id is the same model; thinking is output either way.
 */
const viaAntigravity = (anthropicId: string, aliases: ReadonlyArray<string> = []): ModelPrice => {
  const a = ANTHROPIC_PRICES.find((p) => p.id === anthropicId)
  if (!a) throw new Error(`no Anthropic price for ${anthropicId}`)
  return { ...a, provider: "google", aliases }
}

/**
 * The models Minga uses today (runner CLIs and the cloud mind: `claude-sonnet-5-5`, `gpt-5.5`), and their neighbours,
 * so old rows stay priced, plus what Antigravity (provider "google") and Muse Code (provider "meta") report. Not on
 * the pricing pages and therefore unpriced: `gpt-6`, `gpt-6-mini`, `gpt-5.5-mini`; agy's `gpt-oss-120b-*`; Muse's
 * `muse-wonder-full-1.0` (subscription only, no API price).
 */
export const PRICES: ReadonlyArray<ModelPrice> = [
  ...ANTHROPIC_PRICES,
  // ---- OpenAI (standard tier, ≤ 272k input; the long-context tier is not applied) ----
  openai("gpt-6-astra", 10, 1, 12.5, 50),
  openai("gpt-6.1-sol", 2, 0.1, 2.5, 10),
  openai("gpt-6-sol", 2, 0.2, 2.5, 10),
  openai("gpt-6-luna", 0.1, 0.01, 0.125, 0.5),
  openai("gpt-5.6-sol", 4, 0.4, 5, 20),
  openai("gpt-5.6-terra", 2, 0.2, 2.5, 12),
  openai("gpt-5.6-luna", 0.2, 0.02, 0.25, 1.2),
  openai("gpt-5.5", 5, 0.5, null, 30, 2.5), // retires from Codex 2026-10-14; the cloud mind's OpenAI default
  openai("gpt-5.4", 2.5, 0.25, null, 15),
  openai("gpt-5.4-mini", 0.75, 0.075, null, 4.5),
  openai("gpt-5.3-codex", 1.75, 0.175, null, 14),
  openai("gpt-5", 1.25, 0.125, null, 10),
  // ---- Google: what Antigravity (agy 1.3.6) serves. Paid Tier Standard; cache = implicit cache reads. The page lists
  // scheduled rises: Argon to $4 / $0.20 / $20 from 2026-12-01, 3.8 Flash to $1.50 / $0.15 / $7.50 from 2027-01-01.
  // A row is priced once with the table of its day, so each rise ships as a new table version before that date.
  listed("google", "gemini-4-argon", 2, 0.1, 10, agyLevels("gemini-4-argon")),
  listed("google", "gemini-3.8-flash", 0.75, 0.075, 3.75, agyLevels("gemini-3.8-flash")),
  viaAntigravity("claude-sonnet-4-6"),
  viaAntigravity("claude-opus-4-6", ["claude-opus-4-6-thinking"]),
  // ---- Meta: what Muse Code (1.4.9) serves. Standard mode (Contributor-mode ids are not in Muse's catalog). Web
  // search ($2.50 per 1,000) is not on Muse rows, so it is not priced.
  listed("meta", "muse-wonder-1.0", 4, 0.2, 20),
  listed("meta", "muse-spark-1.3", 1.25, 0.15, 4.25),
  listed("meta", "muse-spark-1.2", 1.25, 0.15, 4.25),
  listed("meta", "muse-spark-1.1", 1.25, 0.15, 4.25),
]

/** Anthropic web search: $10 per 1,000 searches = 10,000 micro-USD each. */
export const WEB_SEARCH_MICRO_USD = 10_000

// #region sandbox compute
/**
 * Cloudflare Containers list prices for Platform Runner task sandboxes (decision 0107). Versioned apart from the model
 * table: every ComputeUsage row keeps the `priceTableVersion` it was priced with.
 *
 * Sources, fetched 2026-10-15:
 * - Pricing         https://developers.cloudflare.com/containers/pricing/ (Workers Paid: $0.000020 per vCPU-second,
 *                   $0.0000025 per GiB-second of memory, $0.00000007 per GB-second of disk, billed per 10 ms while the
 *                   instance runs; memory and disk on the provisioned size, CPU on active use; charges start when the
 *                   container starts and stop when it sleeps. Included per month: 375 vCPU-minutes, 25 GiB-hours of
 *                   memory, 200 GB-hours of disk; egress $0.025/GB in North America and Europe after 1 TB)
 * - Instance types  https://developers.cloudflare.com/containers/platform-details/limits/
 *
 * Minga measures the container's running time, not its CPU use, so CPU is priced at 100% of the instance's vCPUs: an
 * upper bound. The included allowance is account-wide and cannot be attributed to one run, so rows carry the list
 * price and `costMicroUsd: null`. Egress is not measured and not priced.
 */
export const CONTAINER_PRICE_TABLE_VERSION = "containers-2026-10-15"

export const CONTAINER_PRICES = {
  source: "https://developers.cloudflare.com/containers/pricing/",
  fetched: "2026-10-15",
  /** micro-USD per vCPU-hour ($0.000020 per vCPU-second). */
  vcpuHour: 72_000,
  /** micro-USD per GiB-hour of memory ($0.0000025 per GiB-second). */
  memoryGibHour: 9_000,
  /** micro-USD per GB-hour of disk ($0.00000007 per GB-second). */
  diskGbHour: 252,
} as const

export interface ContainerInstance {
  readonly vcpu: number
  readonly memoryMib: number
  /** Decimal GB as Cloudflare lists them. */
  readonly diskGb: number
}

/** Instance types as https://developers.cloudflare.com/containers/platform-details/limits/ lists them (2026-10-15). */
export const CONTAINER_INSTANCES: Readonly<Record<string, ContainerInstance>> = {
  lite: { vcpu: 1 / 16, memoryMib: 256, diskGb: 2 },
  basic: { vcpu: 1 / 4, memoryMib: 1024, diskGb: 4 },
  "standard-1": { vcpu: 1 / 2, memoryMib: 4096, diskGb: 8 },
  "standard-2": { vcpu: 1, memoryMib: 6144, diskGb: 12 },
  "standard-3": { vcpu: 2, memoryMib: 8192, diskGb: 16 },
  "standard-4": { vcpu: 4, memoryMib: 12_288, diskGb: 20 },
}

/** An instance type's upper-bound list rate in micro-USD per running hour (CPU at 100%), rounded up; null if unknown. */
export const containerRateMicroUsdPerHour = (instanceType: string): number | null => {
  const i = CONTAINER_INSTANCES[instanceType]
  if (!i) return null
  return Math.ceil(
    i.vcpu * CONTAINER_PRICES.vcpuHour +
      (i.memoryMib / 1024) * CONTAINER_PRICES.memoryGibHour +
      i.diskGb * CONTAINER_PRICES.diskGbHour,
  )
}

const sizeOf = (instanceType: string): ComputeUsage["size"] =>
  instanceType === "standard-1"
    ? "small"
    : instanceType === "standard-2"
      ? "medium"
      : instanceType === "standard-4"
        ? "large"
        : "custom"

/**
 * One sandbox's compute row for a run: its running time on one instance type at the upper-bound list rate. The
 * platform pays sandboxes (the operator's Cloudflare account), so the row names its payer; `costMicroUsd` stays null
 * (whether the account's included allowance covered it is not knowable per run). An unknown instance type is priced
 * at the largest known rate, marked `estimate`.
 */
export const sandboxComputeRow = (input: {
  readonly instanceType: string
  readonly runningMs: number
  readonly fundedBy: FundedBy
  /** The container instance (Durable Object id) and the measured window, for a later reconciliation. */
  readonly instanceId?: string | undefined
  readonly window?: { readonly from: number; readonly to: number } | undefined
}): ComputeUsage => {
  const known = CONTAINER_INSTANCES[input.instanceType]
  const instance = known ?? (CONTAINER_INSTANCES["standard-4"] as ContainerInstance)
  const rate = containerRateMicroUsdPerHour(known ? input.instanceType : "standard-4") ?? 0
  const runningSeconds = Math.max(0, Math.ceil(input.runningMs / 1000))
  return {
    provider: "cloudflare",
    billingClass: "compute",
    fundedBy: input.fundedBy,
    size: sizeOf(input.instanceType),
    instanceType: input.instanceType.slice(0, 40),
    vcpu: instance.vcpu,
    memoryMib: instance.memoryMib,
    diskMb: instance.diskGb * 1000,
    runningSeconds,
    rateMicroUsdPerHour: rate,
    listMicroUsd: computeListMicroUsd(rate, runningSeconds),
    costMicroUsd: null,
    costSource: known ? "price_table" : "estimate",
    upperBound: true,
    priceTableVersion: CONTAINER_PRICE_TABLE_VERSION,
    ...(input.instanceId ? { instanceId: input.instanceId.slice(0, 80) } : {}),
    ...(input.window ? { window: { from: input.window.from, to: input.window.to } } : {}),
  }
}
// #endregion sandbox compute

const byKey = new Map<string, ModelPrice>()
for (const p of PRICES) {
  byKey.set(`${p.provider}:${p.id}`, p)
  for (const a of p.aliases) byKey.set(`${p.provider}:${a}`, p)
}

/** "claude-opus-5-5[1m]", "anthropic.claude-opus-5-5", "us.anthropic.claude-opus-5-5-v1:0", "claude-opus-4-5-20251101",
 * "openai/GPT-6.1-Sol", "gpt-5-2025-08-07" → the bare lowercase id (not checked against the table). */
const bareModel = (raw: string): string => {
  let m = raw.trim().toLowerCase()
  m = m.replace(/\[[^\]]*\]$/, "") // "[1m]" context suffixes
  m = m.replace(/^(?:[a-z]{2,4}\.)?(?:anthropic|openai|google|meta)[./]/, "") // Bedrock / Vertex / gateway prefixes
  m = m.replace(/-v\d+(?::\d+)?$/, "") // Bedrock "-v1:0"
  m = m.replace(/@\d{8}$/, "") // Vertex "@20251101"
  m = m.replace(/-\d{8}$/, "") // "-20251101"
  m = m.replace(/-\d{4}-\d{2}-\d{2}$/, "") // OpenAI "-2025-08-07"
  return m
}

/** The canonical id of a model in the price table, or null when it is not in the table. */
export const canonicalModel = (provider: ModelProvider, raw: string): string | null =>
  byKey.get(`${provider}:${bareModel(raw)}`)?.id ?? null

export const priceOf = (provider: ModelProvider, model: string): ModelPrice | null =>
  byKey.get(`${provider}:${bareModel(model)}`) ?? null

/** The id a row is grouped under: the canonical id, or the raw id (lowercased, suffixes stripped) when unpriced. */
export const modelKey = (provider: ModelProvider, raw: string): string =>
  canonicalModel(provider, raw) ?? bareModel(raw)

/**
 * List price of a normalized (v2) row in micro-USD, rounded once at the end, or null when the model is unpriced.
 * input (uncached) + cache reads + 1 h writes + 5 m writes + output, at fast prices when `speed === "fast"` (a model
 * without fast prices is priced standard, ≈ low), plus Anthropic web searches. Per-request tiers are not applied.
 */
export const listPriceMicroUsd = (row: UsageV2): number | null => {
  const p = priceOf(row.provider, row.model)
  if (!p) return null
  const write = row.cacheWriteTokens
  const write1h = Math.min(row.cacheWrite1hTokens ?? 0, write)
  const write5m = write - write1h
  let input = p.input
  let cacheRead = p.cacheRead
  let cacheWrite5m = p.cacheWrite5m
  let cacheWrite1h = p.cacheWrite1h
  let output = p.output
  if (row.speed === "fast" && p.fast) {
    if ("multiplier" in p.fast) {
      const k = p.fast.multiplier
      input *= k
      cacheRead *= k
      cacheWrite5m *= k
      cacheWrite1h *= k
      output *= k
    } else {
      const k = p.fast.input / p.input // caching multipliers stack on the fast input price
      input = p.fast.input
      cacheRead *= k
      cacheWrite5m *= k
      cacheWrite1h *= k
      output = p.fast.output
    }
  }
  const tokens =
    row.inputTokens * input +
    row.cachedInputTokens * cacheRead +
    write1h * cacheWrite1h +
    write5m * cacheWrite5m +
    row.outputTokens * output
  const searches = p.provider === "anthropic" ? (row.webSearchRequests ?? 0) * WEB_SEARCH_MICRO_USD : 0
  return Math.round(tokens + searches)
}

/** The legacy `billing` literal written next to `billingClass` (an older cloud and the old web read it). */
export const legacyBilling = (c: BillingClass): "subscription_included" | "metered" | "unpriced" =>
  c === "subscription" ? "subscription_included" : c === "unknown" ? "unpriced" : "metered"

/** Compute list price: rate × running time, rounded up (an upper bound). */
export const computeListMicroUsd = (rateMicroUsdPerHour: number, runningSeconds: number): number =>
  Math.ceil((rateMicroUsdPerHour * runningSeconds) / 3600)

/**
 * Fills `listMicroUsd`, `costMicroUsd`, `costSource`, `priceTableVersion`, `billingClass` and the legacy `billing` of
 * one stored row, pricing its normalized tokens (`v2`, from core `usageRowV2`) and returning the STORED token fields
 * unchanged. Only absent keys are filled (a non-null `costMicroUsd` counts as present), so a vendor row keeps its own
 * figures and a runner's `costSource: "estimate"` stays.
 *
 * | billingClass        | costMicroUsd                                                         | costSource      |
 * | subscription        | null (included; never 0)                                             | as list         |
 * | api_key             | reported.microUsd when basis "managed", else listMicroUsd            | as list         |
 * | subscription_extra  | listMicroUsd (an upper bound: the share past the limit is unknown)  | estimate        |
 * | vendor_credits      | null unless a vendor adapter supplied one                            | vendor          |
 * | unknown             | null                                                                 | as list         |
 * List: `reported` with basis "list" wins (Claude applies cache TTLs, tiers and fast mode per request) unless it is 0
 * for a row with tokens (Claude Code reports 0 for a model it has no price for); else the table; else (unpriced) a
 * non-zero `reported` of basis "unknown", as `estimate` (sdk.d.ts: a guess at the default model's rate); else null.
 * "As list": cli_reported / price_table / estimate / absent.
 */
export const pricedRow = (stored: Usage, v2: UsageV2): Usage => {
  const cls = v2.billingClass
  const reported = stored.reported
  const table = listPriceMicroUsd(v2)
  const hasTokens = v2.inputTokens + v2.cachedInputTokens + v2.cacheWriteTokens + v2.outputTokens > 0
  let list: number | null
  let listSource: "cli_reported" | "price_table" | "estimate" | undefined
  if (reported?.basis === "list" && (reported.microUsd > 0 || !hasTokens)) {
    list = reported.microUsd
    listSource = "cli_reported"
  } else if (table !== null) {
    list = table
    listSource = reported?.basis === "managed" ? "cli_reported" : "price_table"
  } else if (reported?.basis === "unknown" && reported.microUsd > 0) {
    list = reported.microUsd
    listSource = "estimate"
  } else {
    list = null
    listSource = reported ? "cli_reported" : undefined
  }
  const listMicroUsd = stored.listMicroUsd !== undefined ? stored.listMicroUsd : list
  let cost: number | null = null
  switch (cls) {
    case "api_key":
      cost = reported?.basis === "managed" ? reported.microUsd : listMicroUsd
      break
    case "subscription_extra":
      cost = listMicroUsd
      break
    default:
      cost = null
  }
  const costMicroUsd = stored.costMicroUsd !== null ? stored.costMicroUsd : cost
  const source =
    stored.costSource ??
    (cls === "subscription_extra" ? "estimate" : cls === "vendor_credits" ? "vendor" : listSource)
  return {
    ...stored,
    billingClass: cls,
    billing: legacyBilling(cls),
    listMicroUsd,
    costMicroUsd,
    ...(source !== undefined ? { costSource: source } : {}),
    priceTableVersion:
      stored.priceTableVersion !== undefined ? stored.priceTableVersion : PRICE_TABLE_VERSION,
  }
}
